import { GlassCard } from '@/components/design-system';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { updateUsers, type UsersResponse } from '@/lib/collections/users';
import { Check, Copy, Pencil, User, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface HouseholdProfileCardProps {
  userAddress: string;
  profile: UsersResponse | null;
}

type EditableField = 'displayName' | 'city';

/** Same truncation convention used by WalletButton across the app (6 head chars + 4 tail chars). */
function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Household profile card: truncated wallet address, role badge, and
 * inline-editable displayName/city that persist through the users/$userId
 * update collection function (already scoped to the profile owner).
 */
export function HouseholdProfileCard({ userAddress, profile }: HouseholdProfileCardProps) {
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = (field: EditableField, current: string) => {
    setEditingField(field);
    setDraft(current ?? '');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setDraft('');
  };

  const saveEdit = async () => {
    if (!editingField) return;
    const value = draft.trim();
    if (!value) {
      toast.error('This field cannot be empty.');
      return;
    }

    const field = editingField;
    setEditingField(null); // optimistic close — realtime subscription will confirm the new value
    setSaving(true);
    try {
      const payload = field === 'displayName' ? { displayName: value } : { city: value };
      const success = await updateUsers(userAddress, payload);
      if (success) {
        toast.success('Profile updated');
      } else {
        toast.error('Could not save that change — permission denied.');
      }
    } catch (err) {
      console.error('Failed to update profile', err);
      toast.error('Something went wrong saving your profile.');
    } finally {
      setSaving(false);
    }
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(userAddress);
      toast.success('Address copied to clipboard');
    } catch {
      toast.error('Could not copy.');
    }
  };

  const renderField = (field: EditableField, label: string, value: string, placeholder: string) => {
    const isEditing = editingField === field;
    return (
      <div>
        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</label>
        {isEditing ? (
          <div className="mt-1.5 flex items-center gap-2">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={placeholder}
              autoFocus
              className="h-9"
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit();
                if (e.key === 'Escape') cancelEdit();
              }}
            />
            <Button size="icon" className="h-9 w-9 shrink-0" onClick={saveEdit} disabled={saving}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={cancelEdit} disabled={saving}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => startEdit(field, value)}
            className="mt-1.5 flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 bg-secondary/30 px-3 py-2 text-left transition-colors hover:border-primary/40"
          >
            <span className="text-sm font-medium text-foreground">{value || placeholder}</span>
            <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        )}
      </div>
    );
  };

  return (
    <GlassCard padding="lg">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 ring-1 ring-border/60">
            <User className="h-5 w-5 text-primary" strokeWidth={2} />
          </div>
          <div>
            <span className="text-overline">Profile</span>
            <h2 className="font-heading mt-0.5 text-lg font-bold text-foreground">
              {profile?.displayName || 'Household member'}
            </h2>
          </div>
        </div>
        <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary">
          Household
        </Badge>
      </div>

      <button
        type="button"
        onClick={copyAddress}
        className="mt-4 flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 bg-secondary/30 px-3 py-2 text-left transition-colors hover:border-primary/40"
        title="Copy full wallet address"
      >
        <span className="font-mono text-sm text-foreground">{truncateAddress(userAddress)}</span>
        <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {renderField('displayName', 'Display name', profile?.displayName ?? '', 'Add your name')}
        {renderField('city', 'City / location', profile?.city ?? '', 'Add your city')}
      </div>
    </GlassCard>
  );
}

export default HouseholdProfileCard;
